import {
  PRINT_LOGO_WIDTH_DOTS,
  PRINT_RECEIPT_MAX_HEIGHT_DOTS,
  type PrinterDto,
  type PrintReceiptRaster,
} from '@gc/shared';

import { RECEIPT_DOTS, wrapToWidth, type Measure } from '@/features/tabs/receiptLayout';
import { packToPrinterBits, toBase64 } from './printLogo';

/**
 * Settings > Printing > Test — the page staff print, and what the Printing
 * page says while the print bridge answers.
 *
 * The server queues a real TEST job for the venue's print bridge (see
 * `apps/api/src/realtime/printer-test-job.ts`); this file draws the same page
 * so it goes out as dots. Drawn, because the question a test answers is "will
 * this printer print our receipts", and our receipts are drawn: typed text
 * reaches the printer in its own code page, where the Arabic and Kurdish lines
 * print as noise. If the browser cannot draw, the typed page still prints.
 *
 * The three sentences are the server's too, word for word — the typed page is
 * this page without the drawing.
 */

export const PRINTER_TEST_ARABIC = 'إذا كنت تستطيع قراءة هذا، فالطباعة تعمل';
export const PRINTER_TEST_KURDISH = 'ئەگەر دەتوانیت ئەمە بخوێنیتەوە، چاپکردن کار دەکات';
export const PRINTER_TEST_ENGLISH = 'If you can read this, printing works.';

/**
 * `GET /settings/printers/:id/test/:jobId`. Declared here, not in `@gc/shared`
 * (frozen this wave); mirrors `PrinterTestStatusDto` in the API's
 * `printers.service.ts`.
 */
export interface PrinterTestStatus {
  printJobId: string;
  printerId: string;
  status: 'QUEUED' | 'SENT' | 'PRINTED' | 'FAILED';
  /** The bridge's code, or `no_bridge` when the server had none to send to. */
  errorCode: string | null;
  errorMessage: string | null;
  attempts: number;
  drawn: boolean;
  createdAt: string;
  sentAt: string | null;
  printedAt: string | null;
}

/** The bridge's error codes, plus the server's own; anything else reads as unknown. */
export const PRINTER_TEST_ERROR_CODES = [
  'no_bridge',
  'not_found',
  'offline',
  'out_of_paper',
  'timeout',
  'render',
  'unknown',
] as const;
export type PrinterTestErrorCode = (typeof PRINTER_TEST_ERROR_CODES)[number];

/* -------------------------------------------------------------------------- */
/* The page                                                                   */
/* -------------------------------------------------------------------------- */

const MARGIN = 12;

const FONTS = {
  title: 'bold 34px Arial, sans-serif',
  body: '23px Arial, sans-serif',
  script: 'bold 28px Arial, sans-serif',
  closing: 'bold 26px Arial, sans-serif',
} as const;
type FontKey = keyof typeof FONTS;

const LINE_HEIGHT: Record<FontKey, number> = { title: 44, body: 30, script: 40, closing: 36 };

export type TestPageLine = { text: string; font: FontKey } | { rule: true };

export type TestPageBlock =
  | { kind: 'text'; text: string; font: string; y: number; height: number }
  | { kind: 'rule'; y: number; height: number };

export interface TestPageLayout {
  heightDots: number;
  blocks: TestPageBlock[];
}

/** `YYYY-MM-DD HH:MM:SS` on this till's clock, like the drawn tickets. */
export function testPageTime(at: Date): string {
  const pad = (value: number): string => String(value).padStart(2, '0');
  return (
    `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())} ` +
    `${pad(at.getHours())}:${pad(at.getMinutes())}:${pad(at.getSeconds())}`
  );
}

/** Where the page is going, as the Printing page lists the printer. */
export function printerAddress(printer: PrinterDto): string {
  return printer.connection === 'USB'
    ? `USB ${printer.windowsPrinterName ?? '?'}`
    : `TCP ${printer.host ?? '?'}:${printer.port ?? 9100}`;
}

/**
 * A printer as a picker names it. A switched-off printer routes nothing — its
 * tickets fall through to the next printer in line — so a category that points
 * at one must not read as if it still printed there.
 */
export function printerChoiceLabel(
  printer: Pick<PrinterDto, 'name' | 'isActive'>,
  offWord: string,
): string {
  return printer.isActive ? printer.name : `${printer.name} (${offWord})`;
}

/** What the drawn page says, top to bottom. */
export function printerTestPageLines(input: {
  venueName: string;
  printer: PrinterDto;
  requestedBy: string | null;
  at: Date;
}): TestPageLine[] {
  return [
    { text: 'PRINTER TEST', font: 'title' },
    ...(input.venueName ? [{ text: input.venueName, font: 'body' as const }] : []),
    { text: `Printer: ${input.printer.name} (${printerAddress(input.printer)})`, font: 'body' },
    { text: testPageTime(input.at), font: 'body' },
    ...(input.requestedBy
      ? [{ text: `Requested by: ${input.requestedBy}`, font: 'body' as const }]
      : []),
    { rule: true },
    { text: PRINTER_TEST_ARABIC, font: 'script' },
    { text: PRINTER_TEST_KURDISH, font: 'script' },
    { rule: true },
    { text: PRINTER_TEST_ENGLISH, font: 'closing' },
  ];
}

/**
 * Pure: every line centred and wrapped to the paper, measured by the caller's
 * canvas. Kept apart from the painter because no canvas ships in this
 * workspace's tests, and the arithmetic is the part worth pinning.
 */
export function layoutTestPage(lines: readonly TestPageLine[], measure: Measure): TestPageLayout {
  const blocks: TestPageBlock[] = [];
  let y = MARGIN;

  for (const line of lines) {
    if ('rule' in line) {
      blocks.push({ kind: 'rule', y: y + 8, height: 3 });
      y += 19;
      continue;
    }
    const font = FONTS[line.font];
    for (const text of wrapToWidth(line.text, RECEIPT_DOTS - MARGIN * 2, font, measure)) {
      blocks.push({ kind: 'text', text, font, y, height: LINE_HEIGHT[line.font] });
      y += LINE_HEIGHT[line.font];
    }
  }

  return { heightDots: y + MARGIN, blocks };
}

/**
 * The page as printer dots, ready to POST with the test — or null when this
 * browser cannot draw it, in which case the typed page prints instead.
 */
export function printerTestPageToRaster(lines: readonly TestPageLine[]): PrintReceiptRaster | null {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = RECEIPT_DOTS;
    canvas.height = 1;
    const scratch = canvas.getContext('2d');
    if (!scratch) return null;

    const layout = layoutTestPage(lines, (text, font) => {
      scratch.font = font;
      return scratch.measureText(text).width;
    });
    if (layout.heightDots < 1 || layout.heightDots > PRINT_RECEIPT_MAX_HEIGHT_DOTS) return null;

    canvas.height = layout.heightDots;
    const context = canvas.getContext('2d');
    if (!context) return null;

    // Filled after the resize, which clears the canvas; unfilled is transparent.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, RECEIPT_DOTS, layout.heightDots);
    context.fillStyle = '#000000';
    context.textAlign = 'center';
    context.textBaseline = 'top';
    for (const block of layout.blocks) {
      if (block.kind === 'rule') {
        context.fillRect(MARGIN, block.y, RECEIPT_DOTS - MARGIN * 2, block.height);
        continue;
      }
      context.font = block.font;
      // The browser shapes and orders the Arabic script itself — which is the
      // whole reason this page is drawn rather than typed.
      context.fillText(block.text, RECEIPT_DOTS / 2, block.y + 4);
    }

    const { data } = context.getImageData(0, 0, RECEIPT_DOTS, layout.heightDots);
    return {
      widthDots: PRINT_LOGO_WIDTH_DOTS,
      heightDots: layout.heightDots,
      data: toBase64(packToPrinterBits(data, RECEIPT_DOTS, layout.heightDots)),
    };
  } catch {
    return null;
  }
}

/* -------------------------------------------------------------------------- */
/* Following the answer                                                       */
/* -------------------------------------------------------------------------- */

/**
 * How long the page waits for the bridge before saying it never answered.
 *
 * A current bridge makes one attempt at a test, bounded by its 15-second
 * printer timeout. An older one retries three times with pauses — about 50
 * seconds in the worst case — so the page waits that out rather than calling
 * silence early; a failure it has already heard is shown meanwhile.
 */
export const TEST_ANSWER_DEADLINE_MS = 55_000;
export const TEST_POLL_MS = 1_000;

export type TestVerdict =
  | { state: 'sending' }
  | { state: 'waiting' }
  /** `drawn` null: not known (a remembered result — the row keeps no such flag). */
  | { state: 'printed'; at: string | null; drawn: boolean | null }
  | { state: 'failed'; code: PrinterTestErrorCode; detail: string | null }
  | { state: 'noAnswer' }
  | { state: 'error'; message: string };

/** What the page should say about a test, from the job and how long it has been. */
export function testVerdict(status: PrinterTestStatus | null, elapsedMs: number): TestVerdict {
  if (status?.status === 'PRINTED') {
    return { state: 'printed', at: status.printedAt, drawn: status.drawn };
  }
  if (status?.status === 'FAILED') {
    const code = (PRINTER_TEST_ERROR_CODES as readonly string[]).includes(status.errorCode ?? '')
      ? (status.errorCode as PrinterTestErrorCode)
      : 'unknown';
    return { state: 'failed', code, detail: status.errorMessage };
  }
  return elapsedMs >= TEST_ANSWER_DEADLINE_MS ? { state: 'noAnswer' } : { state: 'waiting' };
}

/**
 * Stop following? On paper, on "no bridge" (nothing is coming), or at the
 * deadline. A bridge failure alone is not the end: an older bridge retries, and
 * its later success must still reach the page.
 */
export function testIsSettled(status: PrinterTestStatus | null, elapsedMs: number): boolean {
  if (status?.status === 'PRINTED') return true;
  if (status?.status === 'FAILED' && status.errorCode === 'no_bridge') return true;
  return elapsedMs >= TEST_ANSWER_DEADLINE_MS;
}

/**
 * The last test as the printer row remembers it — shown when this page has not
 * run one itself, so a failed test is still visible after a reload. The page
 * prints it with its date: rows tested before this build carry the old
 * cloud-side probe's verdict ("No answer from …:9100 within 4s"), which said
 * nothing about the printer, and the date is what marks it as that.
 */
export function rememberedVerdict(
  printer: PrinterDto,
  now: number = Date.now(),
): TestVerdict | null {
  if (!printer.lastTestAt) return null;
  if (printer.lastTestOk === true) return { state: 'printed', at: printer.lastTestAt, drawn: null };
  if (printer.lastTestOk === null) {
    // Sent and never answered — waiting only while an answer could still come.
    const age = now - new Date(printer.lastTestAt).getTime();
    return age >= TEST_ANSWER_DEADLINE_MS ? { state: 'noAnswer' } : { state: 'waiting' };
  }
  const match = /^([a-z_]+): (.*)$/s.exec(printer.lastError ?? '');
  const code =
    match && (PRINTER_TEST_ERROR_CODES as readonly string[]).includes(match[1]!)
      ? (match[1] as PrinterTestErrorCode)
      : 'unknown';
  return { state: 'failed', code, detail: match ? match[2]! : printer.lastError };
}
